import React, { useState } from 'react';

// SHA-256 hash of the case-studies password, supplied via environment only —
// no password/hash is committed to the repo. Set REACT_APP_CASES_HASH in
// Netlify (Site settings → Environment variables) for production, and in a
// local .env.local (git-ignored) for development. With no value set the gate
// fails closed (nothing unlocks). To generate a hash, run in a terminal:
//   printf '%s' 'your-password' | shasum -a 256
const EXPECTED_HASH = (process.env.REACT_APP_CASES_HASH || '').toLowerCase();

const STORAGE_KEY = 'cases-unlocked';

async function sha256(text) {
	const data = new TextEncoder().encode(text);
	const digest = await window.crypto.subtle.digest('SHA-256', data);
	return Array.from(new Uint8Array(digest))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
}

const PasswordGate = ({ children }) => {
	const [unlocked, setUnlocked] = useState(
		() => sessionStorage.getItem(STORAGE_KEY) === '1'
	);
	const [value, setValue] = useState('');
	const [error, setError] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const handleSubmit = async (event) => {
		event.preventDefault();
		// trim so a stray leading/trailing space (common when pasting) can't
		// cause a false rejection
		const hash = await sha256(value.trim());
		if (hash === EXPECTED_HASH) {
			sessionStorage.setItem(STORAGE_KEY, '1');
			setUnlocked(true);
			setError(false);
		} else {
			setError(true);
			setValue('');
		}
	};

	if (unlocked) {
		return children;
	}

	return (
		<div className="gate">
			<form className="gate-form" onSubmit={handleSubmit}>
				<p className="eyebrow">Protected</p>
				<h1 className="gate-title">Case studies</h1>
				<p className="gate-text">
					These project case studies are password protected. Enter your
					password below, or{' '}
					<a
						href="https://www.linkedin.com/in/noora-jumppanen/"
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Noora's LinkedIn profile"
					>
						get in touch
					</a>{' '}
					and I’ll be happy to share access.
				</p>
				<div className="gate-input-wrap">
					<input
						className="gate-input"
						type={showPassword ? 'text' : 'password'}
						value={value}
						onChange={(event) => setValue(event.target.value)}
						placeholder="Password"
						aria-label="Password"
						autoFocus
					/>
					<button
						type="button"
						className="gate-toggle"
						onClick={() => setShowPassword((shown) => !shown)}
						aria-pressed={showPassword}
						aria-label={showPassword ? 'Hide password' : 'Show password'}
					>
						{showPassword ? 'Hide' : 'Show'}
					</button>
				</div>
				{error && (
					<p className="gate-error" role="alert">
						Incorrect password. Please try again.
					</p>
				)}
				<button className="gate-button" type="submit">
					Unlock
				</button>
			</form>
		</div>
	);
};

export default PasswordGate;
